/**
 * Where a sibling application lives. One image serves every environment, so this is decided at runtime, not at build
 * time: an explicit URL wins; the Vite dev server goes to the local app; otherwise the app lives beside this one on the
 * same domain with the first host label swapped (orders.ghezaa-me.com -> core.ghezaa-me.com).
 */
function siblingUrl(label: string, configured: string | undefined, isDev: boolean, devOrigin: string): string {
  if (configured) return configured;
  if (isDev) return devOrigin;
  const { protocol, hostname, port } = window.location;
  const labels = hostname.split(".");
  const host = labels.length > 2 ? [label, ...labels.slice(1)].join(".") : hostname;
  return `${protocol}//${host}${port ? `:${port}` : ""}`;
}

/**
 * Where "Back to Core" points: VITE_CORE_URL, the local Core in dev, else core.<domain>. The caller passes its own
 * `import.meta.env` values, which keeps this library free of Vite's types.
 */
export function coreUrl(configured: string | undefined, isDev: boolean): string {
  return siblingUrl("core", configured, isDev, "http://localhost:5173");
}

/** Where Support lives, for a link to Support from other apps: VITE_SUPPORT_URL, the local Support in dev, else support.<domain>. */
export function supportUrl(configured: string | undefined, isDev: boolean): string {
  return siblingUrl("support", configured, isDev, "http://localhost:5175");
}

/** The local Vite port of each application (CLAUDE.md), so the switcher stays on this machine in development. */
const LOCAL_PORTS: Record<string, number> = { core: 5173, orders: 5174, support: 5175, dnotes: 5176, sourcing: 5177 };

/**
 * Where the switcher opens an application, from its registry URL: absolute in production, a path for Core itself (a
 * path then lives on the application's own host, core.<domain>). On localhost a known application opens on its local
 * port, keeping the path (Orders' "/staff").
 */
export function applicationUrl(code: string, url: string): string {
  const relative = url.startsWith("/");
  const local = window.location.hostname === "localhost" ? LOCAL_PORTS[code] : undefined;
  if (local) {
    const path = relative ? url : pathOf(url);
    return `http://localhost:${local}${path === "/" ? "" : path}`;
  }
  return relative ? `${siblingUrl(code, undefined, false, "")}${url === "/" ? "" : url}` : url;
}

function pathOf(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return "/";
  }
}
